import { getLocalStorageItemAsString, setLocalStorageItem } from '../local-storage';

export const RECENT_SEARCH_KEY = 'atlassian.unified-search.recent-searches';

export const MAX_RECENT_SEARCHES_STORED = 50;

export type RecentSearchObject = {
	query: string;
	metadata?: {
		intentMode?: 'FPS' | 'GNIN';
	};
};
export type RecentSearchItem = string | RecentSearchObject;

type recentSearches = {
	[key: string]: { [key: string]: RecentSearchItem[] };
};

// Helper function to get the query string from a RecentSearchItem
export const getRecentQueryObject = (item: RecentSearchItem): RecentSearchObject =>
	typeof item === 'string' ? { query: item } : item;

type RecentSearchProps = {
	userId: string;
	productId: string;
	key: string;
};

type RecentSearchActionProps = {
	value: RecentSearchItem;
} & RecentSearchProps;

export const addRecentSearch = ({
	value,
	userId,
	productId,
	key,
}: RecentSearchActionProps): void => {
	const recentSearchesRaw = getLocalStorageItemAsString(key);
	const recentSearches: recentSearches = recentSearchesRaw ? JSON.parse(recentSearchesRaw) : {};

	if (!recentSearches[userId]) {
		recentSearches[userId] = {};
	}
	let userSearches = recentSearches[userId];

	if (!userSearches[productId]) {
		userSearches[productId] = [];
	}
	let productSearches = userSearches[productId];

	// Normalize the value: if it's a string, trim it; if it's an object, trim the query
	const normalizedValue: RecentSearchItem =
		typeof value === 'string' ? value.trim() : { ...value, query: value.query.trim() };

	// Pop the last value of the array if it is too long
	if (productSearches.length === MAX_RECENT_SEARCHES_STORED) {
		productSearches.pop();
	}

	// If the given value is a duplicate (based on query), remove it from the array
	const { query: normalizedQuery } = getRecentQueryObject(normalizedValue);
	productSearches = productSearches.filter(
		(v) => getRecentQueryObject(v).query !== normalizedQuery,
	);

	productSearches.unshift(normalizedValue);

	recentSearches[userId][productId] = productSearches;

	setLocalStorageItem(key, JSON.stringify(recentSearches));
};

export const getRecentSearches = ({
	userId,
	productId,
	key,
}: RecentSearchProps): RecentSearchItem[] => {
	const recentSearchesRaw = getLocalStorageItemAsString(key);
	const recentSearches = recentSearchesRaw ? (JSON.parse(recentSearchesRaw) as recentSearches) : {};
	const userSearches = recentSearches[userId] || {};
	return userSearches[productId] || [];
};

export const removeRecentSearch = ({
	value,
	userId,
	productId,
	key,
}: RecentSearchActionProps): RecentSearchItem[] => {
	const recentSearchesRaw = getLocalStorageItemAsString(key);
	const recentSearches: recentSearches = recentSearchesRaw ? JSON.parse(recentSearchesRaw) : {};

	if (!recentSearches[userId] || !recentSearches[userId][productId]) {
		return [];
	}

	const { query: queryToRemove } = getRecentQueryObject(value);
	recentSearches[userId][productId] = recentSearches[userId][productId].filter(
		(v) => getRecentQueryObject(v).query !== queryToRemove,
	);

	setLocalStorageItem(key, JSON.stringify(recentSearches));

	return recentSearches[userId][productId];
};
